import React, { memo, useEffect, useMemo, useState } from 'react';

import Notification from '@plasma/ui.feedback.notification';
import Button from '@plasma/ui.general.button';
import Input from '@plasma/ui.input.input';
import Select from '@plasma/ui.input.select';
import Modal from '@plasma/ui.layout.modal';

import LoadingComponentScalable from '../../../../components/Loding-component/LoadingComponentMini';
import { fetchAllDataGet_byApiName } from '../../../../services/gets/getdata';
import { sendAllDataPost_byApiNameAndData } from '../../../../services/posts/postdata';
import { GuideInstalledPosition, GuideRequirement, ProfileData, RequiredGuidesResponse, StandGuideRequirement } from '../../../../types/guideMasterDatatype';
import * as styled from './guide-installed-position-form.styles';
import { apiUriGuideDataHttp, apiUriMasterDataHttp } from '../../../../services/conf/apiConf';

export interface GuideInstalledPositionFormProps {
  visible: boolean;
  onClose: () => void;
  onSaved: (positions: GuideInstalledPosition[]) => void;
}

type SelectionMap = Record<string, string | undefined>;

const positionKey = (standName: string, guideName: string) => `${standName}||${guideName}`;

function GuideInstalledPositionForm(props: GuideInstalledPositionFormProps) {
  const { visible, onClose, onSaved } = props;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [profileOptions, setProfileOptions] = useState<ProfileData[]>([]);
  const [existingPositions, setExistingPositions] = useState<GuideInstalledPosition[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<ProfileData | null>(null);
  const [requiredGuides, setRequiredGuides] = useState<RequiredGuidesResponse | null>(null);
  const [selections, setSelections] = useState<SelectionMap>({});
  const [selectedProductType, setSelectedProductType] = useState<string | null>(null);
  const [selectedDimension, setSelectedDimension] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    setSelectedProductType(null);
    setSelectedDimension(null);
    setSelectedProfile(null);
    setRequiredGuides(null);
    setSelections({});
    setEditing(false);
    loadInitialData();
  }, [visible]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [profiles, positions] = await Promise.all([
        fetchAllDataGet_byApiName('GetMasterProductDetails', apiUriMasterDataHttp()) as Promise<ProfileData[]>,
        fetchAllDataGet_byApiName('GetGuideInstalledPositions', apiUriGuideDataHttp()) as Promise<GuideInstalledPosition[]>,
      ]);
      setProfileOptions(profiles || []);
      setExistingPositions(positions || []);
      setEditing((positions || []).length === 0);
    } catch (error) {
      console.error('Failed to load installed-position form data', error);
      Notification.error('Error', 'Failed to load form data', undefined, 'medium', 3);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSelect = async (productDescription: string | null) => {
    if (!productDescription) {
      setSelectedProfile(null);
      setRequiredGuides(null);
      setSelections({});
      return;
    }

    const profile = profileOptions.find((item) => item.productdescription === productDescription) || null;
    setSelectedProfile(profile);
    setRequiredGuides(null);
    setSelections({});

    setLoading(true);
    try {
      const query = encodeURIComponent(productDescription);
      const response = (await fetchAllDataGet_byApiName(
        `GetRequiredGuides?productDescription=${query}`,
        apiUriGuideDataHttp(),
      )) as RequiredGuidesResponse;
      setRequiredGuides(response);

      // Pre-fill from existing installed positions, only when that serial is actually a valid
      // candidate for this profile's guide at that stand/direction.
      const initialSelections: SelectionMap = {};
      (response?.stands || []).forEach((stand) => {
        [stand.entryGuide, stand.exitGuide].forEach((guide) => {
          if (!guide) return;

          const existing = existingPositions.find(
            (item) => item.standName === stand.standName && item.guideName === guide.guideName,
          );
          const isValidSerial =
            !!existing?.serialNumber && guide.serials.some((serial) => serial.componentCode === existing.serialNumber);

          if (isValidSerial) {
            initialSelections[positionKey(stand.standName, guide.guideName)] = existing!.serialNumber as string;
          }
        });
      });
      setSelections(initialSelections);
    } catch (error) {
      console.error('Failed to load required guides for profile', error);
      Notification.error('Error', 'Failed to load guides for the selected profile', undefined, 'medium', 3);
    } finally {
      setLoading(false);
    }
  };

  const handleProductSelect = (productType: string | null) => {
    setSelectedProductType(productType);
    setSelectedDimension(null);
    setSelectedProfile(null);
    setRequiredGuides(null);
    setSelections({});
  };

  const handleDimensionSelect = (dimension: string | null) => {
    setSelectedDimension(dimension);
    handleProfileSelect(dimension);
  };

  const handleSerialSelect = (standName: string, guideName: string, serialNumber: string | null) => {
    const currentKey = positionKey(standName, guideName);

    if (serialNumber) {
      // Find if this serial number is already selected elsewhere in the form
      const existingEntry = Object.entries(selections).find(([key, selectedSerial]) => key !== currentKey && selectedSerial === serialNumber);

      if (existingEntry) {
        // Extract the stand name and guide name from the key (e.g., "Stand 1||Entry Guide")
        const [existingStandName, existingGuideName] = existingEntry[0].split('||');

        Notification.warning(
          'Duplicate Serial Selection',
          `Serial ${serialNumber} is already selected for ${existingStandName} (${existingGuideName}).`,
          undefined,
          'large',
        );
        return;
      }
    }

    // Update selections state if no duplicate was found
    setSelections((prev) => ({...prev, [currentKey]: serialNumber || undefined}));
  };

  const handleSave = async () => {
    if (!selectedProfile || !requiredGuides) {
      Notification.info('Select a profile', 'Please select a product profile first', undefined, 'medium', 3);
      return;
    }

    const payload: GuideInstalledPosition[] = [];
    requiredGuides.stands.forEach((stand) => {
      [stand.entryGuide, stand.exitGuide].forEach((guide) => {
        if (!guide) return;

        const key = positionKey(stand.standName, guide.guideName);
        const serialNumber = selections[key] ?? null;
        const matchedSerial = guide.serials.find((serial) => serial.componentCode === serialNumber);

        payload.push({
          standName: stand.standName,
          guideName: guide.guideName,
          serialNumber,
          productType: selectedProfile.producttype,
          productDescription: selectedProfile.productdescription,
        });
      });
    });

    setSaving(true);
    try {
      const saved = (await sendAllDataPost_byApiNameAndData(
        'SaveGuideInstalledPositions',
        payload,
        apiUriGuideDataHttp(),
      )) as GuideInstalledPosition[];
      setExistingPositions(saved || []);
      setEditing(false);
      Notification.success('Saved', 'Installed guide positions updated', undefined, 'medium', 3);
      onSaved(saved || []);
    } catch (error) {
      console.error('Failed to save installed guide positions', error);
      Notification.error('Error', 'Failed to save installed guide positions', undefined, 'medium', 3);
    } finally {
      setSaving(false);
    }
  };

  const sortedStands = useMemo(() => requiredGuides?.stands ?? [], [requiredGuides]);

  const groupedInstalledPositions = useMemo(() => {
    const byStand = new Map<
      string,
      { standName: string; entry?: GuideInstalledPosition; exit?: GuideInstalledPosition }
    >();

    existingPositions.forEach((position) => {
      if (!byStand.has(position.standName)) {
        byStand.set(position.standName, { standName: position.standName });
      }
      const row = byStand.get(position.standName)!;
      if (position.guideName.toLowerCase().includes('entry')) {
        row.entry = position;
      } else if (position.guideName.toLowerCase().includes('exit')) {
        row.exit = position;
      }
    });

    return Array.from(byStand.values()).sort((a, b) =>
      a.standName.localeCompare(b.standName, undefined, { numeric: true }),
    );
  }, [existingPositions]);

  const renderPositionValue = (position?: GuideInstalledPosition) => {
    if (!position?.serialNumber) {
      return <span>{'-'}</span>;
    }
    return <span>{position.serialNumber}</span>;
  };

  const currentProfileLabel = useMemo(() => {
    const withProfile = existingPositions.find((position) => position.productType || position.productDescription);
    if (!withProfile) return null;
    return [withProfile.productType, withProfile.productDescription].filter(Boolean).join(' ');
  }, [existingPositions]);

  const handleEnterEditMode = () => {
    setEditing(true);
    if (!selectedProfile) {
      const withProfile = existingPositions.find((position) => position.productDescription);
      if (withProfile?.productDescription) {
        handleProfileSelect(withProfile.productDescription);
        const profile = profileOptions.find((p) => p.productdescription === withProfile.productDescription);
        if (profile) {
          setSelectedProductType(profile.producttype);
          setSelectedDimension(profile.productdescription);
        }
      }
    } else {
      setSelectedProductType(selectedProfile.producttype);
      setSelectedDimension(selectedProfile.productdescription);
    }
  };

  const renderSerialSelect = (stand: StandGuideRequirement, guide?: GuideRequirement | null) => {
    if (!guide) {
      return <span>{'-'}</span>;
    }

    if (guide.serials.length === 0) {
      return (
        <styled.NoSerialText title={`Guide Type: ${guide.guideTypeCode}`}>
          No serials recorded for this guide type
        </styled.NoSerialText>
      );
    }

    const key = positionKey(stand.standName, guide.guideName);

    return (
      <Select
        allowClear
        showSearch
        value={selections[key] || null}
        placeholder={guide.guideTypeCode}
        onChange={(value: string | null) => handleSerialSelect(stand.standName, guide.guideName, value)}
        style={{ width: '100%' }}
      >
        {guide.serials.map((serial) => (
          <Select.Option key={serial.componentCode} value={serial.componentCode}>
            {serial.componentCode}
          </Select.Option>
        ))}
      </Select>
    );
  };

  return (
    <Modal
      title="Manage Installed Guides"
      width="70%"
      visible={visible}
      onCancel={onClose}
      onOk={handleSave}
      okText={saving ? 'Saving...' : 'Save'}
      cancelText="Cancel"
      confirmLoading={saving}
      okButtonProps={{ disabled: !editing || !selectedProfile || sortedStands.length === 0 || saving }}
      destroyOnClose
    >
      <styled.FormContainer>
        <styled.HeadingRow>
          <styled.SectionHeading>{editing ? 'Last Used Guides' : 'Currently Installed Guides'}</styled.SectionHeading>
          {editing ? (
            existingPositions.length > 0 && (
              <Button
                type="tertiary"
                size="small"
                title="View Installed Guides"
                prefixIcon={{ name: 'visibility' }}
                onClick={() => setEditing(false)}
              />
            )
          ) : (
            <Button
              title="Edit Installed Guides"
              type="tertiary"
              size="small"
              prefixIcon={{ name: 'edit' }}
              onClick={handleEnterEditMode}
            />
          )}
        </styled.HeadingRow>

        {loading ? 
          <LoadingComponentScalable borderWidth={3} containerSize={60} message='loading...' /> : 
          !editing && ( <>
            <styled.HintText>
              Current Profile: <strong>{`${currentProfileLabel}`}</strong>
            </styled.HintText>
            <styled.CurrentDataTable>
              <thead>
                <tr>
                  <th>Stand</th>
                  <th>Entry Guide Serial</th>
                  <th>Exit Guide Serial</th>
                </tr>
              </thead>
              <tbody>
                {groupedInstalledPositions.map((row) => (
                  <tr key={row.standName}>
                    <td>{row.standName}</td>
                    <td>{renderPositionValue(row.entry)}</td>
                    <td>{renderPositionValue(row.exit)}</td>
                  </tr>
                ))}
              </tbody>
            </styled.CurrentDataTable>
          </>
        )}

        {editing && (
          <>
            <styled.ProfileFieldWrapper>
              <styled.FieldGroup>
                <label>Product</label>
                <Select
                  allowClear
                  showSearch
                  value={selectedProductType || null}
                  placeholder="Select product"
                  onChange={handleProductSelect}
                  style={{ width: '100%' }}
                >
                  {Array.from(new Set(profileOptions.map((p) => p.producttype))).map((type) => (
                    <Select.Option key={type} value={type}>
                      {type}
                    </Select.Option>
                  ))}
                </Select>
              </styled.FieldGroup>

              <styled.FieldGroup>
                <label>Dimension</label>
                <Select
                  allowClear
                  showSearch
                  value={selectedDimension || null}
                  placeholder="Select Dimension"
                  onChange={handleDimensionSelect}
                  style={{ width: '100%' }}
                >
                  {profileOptions
                    .filter((p) => p.producttype === selectedProductType)
                    .map((profile) => (
                      <Select.Option key={profile.productid} value={profile.productdescription}>
                        {profile.productdescription}
                      </Select.Option>
                    ))}
                </Select>
              </styled.FieldGroup>

              <styled.FieldGroup>
                <label>Profile</label>
                <Input
                  value={selectedProductType && selectedDimension ? `${selectedProductType} ${selectedDimension}` : ''}
                  readOnly
                  placeholder=""
                  style={{ width: '100%' }}
                />
              </styled.FieldGroup>
            </styled.ProfileFieldWrapper>


            {selectedProfile && sortedStands.length === 0 && (
              <styled.HintText>
                No guides are defined for this profile.
              </styled.HintText>
            )}

            {sortedStands.length > 0 && (
              <styled.StandSelectionTable>
                <thead>
                  <tr>
                    <th>Stand</th>
                    <th>Entry Guide Serial</th>
                    <th>Exit Guide Serial</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedStands.map((stand) => (
                    <tr key={stand.standName}>
                      <td>{stand.standName}</td>
                      <td>{renderSerialSelect(stand, stand.entryGuide)}</td>
                      <td>{renderSerialSelect(stand, stand.exitGuide)}</td>
                    </tr>
                  ))}
                </tbody>
              </styled.StandSelectionTable>
            )}
          </>
        )}
      </styled.FormContainer>
    </Modal>
  );
}

export default memo(GuideInstalledPositionForm) as unknown as typeof GuideInstalledPositionForm;
